import { useT } from '../lib/i18n'

interface SpinnerProps {
  size?: number
  /**
   * Announced to a screen reader. Defaults to the dictionary's "loading"; pass
   * `null` when a parent already labels the wait.
   */
  label?: string | null
}

/**
 * A rotating arc.
 *
 * There are no skeletons in this system (design system § Components). A
 * skeleton promises a shape before the shape is known, and this app's screens
 * change height with the data — a skeleton would guess wrong and then jump.
 *
 * Under `prefers-reduced-motion` the rotation is replaced by a pulse rather than
 * removed: a user who suppresses motion still needs to know something is
 * happening. `currentColor` throughout, so the spinner inherits whatever it sits
 * in — including `--on-accent` inside a coral button.
 *
 * SUAP-0103.
 */
export function Spinner({ size = 20, label }: SpinnerProps) {
  const { t } = useT()
  const announced = label === null ? null : (label ?? t('state.loading'))

  return (
    <span
      role={announced === null ? undefined : 'status'}
      aria-label={announced ?? undefined}
      className="inline-flex shrink-0 items-center justify-center"
      style={{ width: size, height: size }}
    >
      <svg
        viewBox="0 0 24 24"
        width={size}
        height={size}
        aria-hidden
        className="motion-safe:animate-spin motion-reduce:animate-pulse"
      >
        <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="3" opacity="0.25" />
        <path
          d="M21 12a9 9 0 0 0-9-9"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </svg>
    </span>
  )
}
